/*──────────────────────────────────────────────────────────────
 * src/pages/BucketGalleryPage.jsx     – regenerated 2025-07-06
 *
 * • For every immediate Run-Ticket folder we fetch **both**
 *     • payload.ocr.json  →  free-text + per-file index
 *     • meta.json         →  lease/user/sha1/etc.
 * • Search bar matches:
 *     • folder name
 *     • words in payload.ocr.json      (global + per-file)
 *     • words in meta.json (all values)
 *     • file name, date, size, file-level OCR
 *──────────────────────────────────────────────────────────────*/

import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { listFiles, signedUrl } from "../utils/FileManager";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFolder,
  faArrowLeft,
  faSpinner,
  faTimes,
  faChevronLeft,
  faChevronRight,
  faSearch,
  faSortAmountDown,
  faFilePdf,
} from "@fortawesome/free-solid-svg-icons";

/* ───────── helpers ──────────────────────────────────────────*/
const isImage = (k) => /\.(jpe?g|png|gif|webp|bmp|avif)$/i.test(k);
const isPdf = (k) => /\.pdf$/i.test(k);

/* ───────── caches ───────────────────────────────────────────*/
const sidecarCache = new Map(); // prefix → { ocrText, index, metaText }
const listCache = new Map(); // prefix → { dirs:[…], files:[…] }

/*─────────────────────────────────────────────────────────────*/
const BucketGalleryPage = () => {
  /* navigation / data */
  const [prefix, setPrefix] = useState(""); // e.g. "src/RunTicket/"
  const [loading, setLoading] = useState(true);

  /* ui state */
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("name"); // name | date | size
  const [sortAsc, setSortAsc] = useState(true);

  /* modal */
  const [activeIdx, setActiveIdx] = useState(null);
  const imgGridRef = useRef(null);

  /*──────────────── fetch helpers ────────────────────────────*/
  const ensureSidecars = useCallback(async (pref) => {
    if (sidecarCache.has(pref)) return sidecarCache.get(pref);

    const entry = { ocrText: "", index: {}, metaText: "" };

    if (pref) {
      const base = pref.replace(/\/?$/, "/");

      /* payload.ocr.json */
      try {
        const ocrUrl = await signedUrl(base + "payload.ocr.json");
        const resp = await fetch(ocrUrl);
        if (resp.ok) {
          const j = await resp.json();
          entry.ocrText = (j.text || "").toLowerCase();
          entry.index = j.index || {};
        }
      } catch {
        /* absent OK */
      }

      /* meta.json → flatten all values */
      try {
        const metaUrl = await signedUrl(base + "meta.json");
        const resp = await fetch(metaUrl);
        if (resp.ok) {
          const j = await resp.json();
          entry.metaText = Object.values(j).join(" ").toLowerCase();
        }
      } catch {
        /* absent OK */
      }
    }

    sidecarCache.set(pref, entry);
    return entry;
  }, []);

  /*──────────────── load prefix (dirs + files) ───────────────*/
  const loadPrefix = useCallback(
    async (p) => {
      if (listCache.has(p)) return;
      setLoading(true);

      try {
        /* 1) list objects */
        const objs = await listFiles(p);
        const dirSet = new Set();
        const files = [];

        objs.forEach(({ Key, LastModified, Size }) => {
          const rel = Key.slice(p.length);
          const sep = rel.indexOf("/");

          if (sep !== -1) {
            dirSet.add(rel.slice(0, sep));
          } else if (isImage(Key) || isPdf(Key)) {
            files.push({
              key: Key,
              name: rel,
              date: LastModified ? new Date(LastModified) : null,
              size: Size,
              kind: isPdf(Key) ? "pdf" : "img",
              ocr: "", // set below
            });
          }
        });

        /* 2) presign file URLs */
        const urls = await Promise.all(files.map(({ key }) => signedUrl(key)));
        files.forEach((f, i) => (f.url = urls[i]));

        /* 3) sidecars of *this* folder */
        const thisSidecar = await ensureSidecars(p);

        /* 4) per-file OCR (index > folder-text) */
        files.forEach((f) => {
          const specific = thisSidecar.index[f.name] || "";
          f.ocr = (specific || thisSidecar.ocrText).toLowerCase();
        });

        /* 5) build dir list with their sidecar search text */
        const dirs = await Promise.all(
          [...dirSet].sort().map(async (d) => {
            const dirPref = `${p}${d}/`;
            const sidecar = await ensureSidecars(dirPref);
            return {
              name: d,
              ocrText: sidecar.ocrText,
              metaText: sidecar.metaText,
            };
          })
        );

        /* 6) cache */
        listCache.set(p, { dirs, files });
      } catch (err) {
        console.error("Gallery load", err);
      } finally {
        setLoading(false);
      }
    },
    [ensureSidecars]
  );

  useEffect(() => {
    loadPrefix(prefix);
  }, [prefix, loadPrefix]);

  /* ready data snapshot */
  const data = listCache.get(prefix) || { dirs: [], files: [] };

  /*──────────────────── filter + sort ───────────────────────*/
  const [dirHits, fileHits] = useMemo(() => {
    const q = search.trim().toLowerCase();

    /* folders */
    const dirs = !q
      ? data.dirs
      : data.dirs.filter(
          ({ name, ocrText, metaText }) =>
            name.toLowerCase().includes(q) ||
            ocrText.includes(q) ||
            metaText.includes(q)
        );

    /* files */
    let files = data.files;
    if (q) {
      files = files.filter(
        ({ name, date, size, ocr }) =>
          name.toLowerCase().includes(q) ||
          ocr.includes(q) ||
          (date && date.toLocaleDateString().includes(q)) ||
          ("" + size).includes(q)
      );
    }
    files = [...files].sort((a, b) => {
      const dir = sortAsc ? 1 : -1;
      if (sortBy === "name") return a.name.localeCompare(b.name) * dir;
      if (sortBy === "date") return ((a.date || 0) - (b.date || 0)) * dir;
      if (sortBy === "size") return (a.size - b.size) * dir;
      return 0;
    });

    return [dirs, files];
  }, [data.dirs, data.files, search, sortBy, sortAsc]);

  /*──────── modal helpers ───────*/
  const openModal = (i) => setActiveIdx(i);
  const closeModal = () => setActiveIdx(null);
  const navModal = (d) =>
    setActiveIdx((i) =>
      i === null ? null : (i + d + fileHits.length) % fileHits.length
    );

  /* keyboard nav */
  useEffect(() => {
    const h = (e) => {
      if (activeIdx === null) return;
      if (e.key === "Escape") closeModal();
      if (e.key === "ArrowRight") navModal(1);
      if (e.key === "ArrowLeft") navModal(-1);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [activeIdx, fileHits.length]);

  /* autoscroll on prefix change */
  useEffect(() => {
    imgGridRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [prefix]);

  const breadcrumb = prefix ? prefix.slice(0, -1).split("/") : [];

  /*──────────────────────── UI ───────────────────────────────*/
  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <h1 className="text-3xl font-bold mb-6 text-center">Bucket Browser</h1>

      {/* breadcrumb */}
      <nav className="text-sm mb-6 flex flex-wrap gap-1 justify-center text-blue-700">
        <span
          className="cursor-pointer hover:underline"
          onClick={() => setPrefix("")}
        >
          root
        </span>
        {breadcrumb.map((seg, i) => {
          const upto = breadcrumb.slice(0, i + 1).join("/") + "/";
          return (
            <React.Fragment key={upto}>
              <span>/</span>
              <span
                className="cursor-pointer hover:underline"
                onClick={() => setPrefix(upto)}
              >
                {seg}
              </span>
            </React.Fragment>
          );
        })}
      </nav>

      {/* search + sort */}
      <div className="flex flex-col md:flex-row md:items-center gap-4 mb-8">
        <label className="relative flex-1">
          <FontAwesomeIcon
            icon={faSearch}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            placeholder="Search file / folder / OCR / meta…"
            className="w-full pl-10 pr-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>

        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faSortAmountDown} />
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="border rounded-lg py-2 px-3"
          >
            <option value="name">Name</option>
            <option value="date">Date</option>
            <option value="size">Size</option>
          </select>
          <button
            className="px-3 py-2 border rounded-lg"
            onClick={() => setSortAsc((b) => !b)}
            title="Toggle sort direction"
          >
            {sortAsc ? "↑" : "↓"}
          </button>
        </div>
      </div>

      {/* back button */}
      {prefix && (
        <button
          className="mb-6 flex items-center gap-2 text-blue-600 hover:underline"
          onClick={() => setPrefix(prefix.replace(/[^/]+\/$/, ""))}
        >
          <FontAwesomeIcon icon={faArrowLeft} /> Back
        </button>
      )}

      {/* loading */}
      {loading && (
        <div className="flex justify-center items-center py-20">
          <FontAwesomeIcon
            icon={faSpinner}
            spin
            className="h-10 w-10 text-blue-600"
          />
        </div>
      )}

      {/* empty */}
      {!loading && dirHits.length === 0 && fileHits.length === 0 && (
        <p className="text-center text-gray-500">No folders or files.</p>
      )}

      {/* folders */}
      {dirHits.length > 0 && (
        <>
          <h2 className="text-xl font-semibold mt-2 mb-4">Folders</h2>
          <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
            {dirHits.map((d) => (
              <div
                key={d.name}
                className="group cursor-pointer flex flex-col items-center justify-center p-4 border rounded-lg bg-white shadow hover:bg-blue-50"
                onClick={() => setPrefix(prefix + d.name + "/")}
              >
                <FontAwesomeIcon
                  icon={faFolder}
                  className="h-8 w-8 text-yellow-500 mb-2 group-hover:text-yellow-600"
                />
                <span className="truncate max-w-full">{d.name}</span>
              </div>
            ))}
          </div>
        </>
      )}

      {/* files */}
      {fileHits.length > 0 && (
        <>
          <h2
            id="grid-start"
            ref={imgGridRef}
            className="text-xl font-semibold mt-10 mb-4"
          >
            Files ({fileHits.length})
          </h2>
          <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
            {fileHits.map((f, idx) =>
              f.kind === "img" ? (
                <img
                  key={f.key}
                  src={f.url}
                  alt={f.name}
                  loading="lazy"
                  className="w-full h-32 object-cover rounded-lg shadow cursor-pointer transition hover:opacity-80"
                  onClick={() => openModal(idx)}
                />
              ) : (
                <div
                  key={f.key}
                  className="w-full h-32 flex flex-col justify-center items-center rounded-lg shadow bg-red-50 text-red-700 cursor-pointer hover:bg-red-100"
                  onClick={() => openModal(idx)}
                >
                  <FontAwesomeIcon icon={faFilePdf} className="h-8 w-8 mb-2" />
                  <span className="text-xs px-2 text-center truncate">
                    {f.name}
                  </span>
                </div>
              )
            )}
          </div>
        </>
      )}

      {/* modal */}
      {activeIdx !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={(e) => e.target === e.currentTarget && closeModal()}
        >
          <button
            className="absolute top-20 right-20 text-white bg-black/60 hover:bg-black/80 rounded-full p-4 backdrop-blur-lg shadow-lg focus:outline-none"
            onClick={closeModal}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faTimes} className="h-6 w-6" />
          </button>

          <button
            className="absolute left-6 text-white/90 hover:text-white text-4xl"
            onClick={() => navModal(-1)}
            aria-label="Previous (←)"
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>

          <button
            className="absolute right-6 text-white/90 hover:text-white text-4xl"
            onClick={() => navModal(1)}
            aria-label="Next (→)"
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>

          <div className="max-w-full max-h-full p-4 flex flex-col md:flex-row items-center gap-6">
            {fileHits[activeIdx].kind === "img" ? (
              <img
                src={fileHits[activeIdx].url}
                alt="preview"
                className="max-h-[70vh] max-w-[90vw] object-contain rounded-lg shadow-2xl"
              />
            ) : (
              <embed
                src={fileHits[activeIdx].url}
                type="application/pdf"
                className="max-h-[70vh] max-w-[90vw] rounded-lg shadow-2xl"
              />
            )}

            <div className="w-[18rem] bg-white/90 backdrop-blur rounded-lg shadow-lg p-4 text-sm">
              <p className="break-all">
                <span className="font-semibold">Name:</span>{" "}
                {fileHits[activeIdx].name}
              </p>
              {fileHits[activeIdx].date && (
                <p>
                  <span className="font-semibold">Date:</span>{" "}
                  {fileHits[activeIdx].date.toLocaleString()}
                </p>
              )}
              <p>
                <span className="font-semibold">Size:</span>{" "}
                {(fileHits[activeIdx].size / 1024).toFixed(1)} KB
              </p>
              {fileHits[activeIdx].ocr && (
                <p className="mt-2 max-h-24 overflow-y-auto whitespace-pre-wrap bg-gray-100 p-2 rounded text-xs">
                  {fileHits[activeIdx].ocr}
                </p>
              )}
              <a
                href={fileHits[activeIdx].url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-3 text-blue-600 underline hover:text-blue-800"
              >
                Open original
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BucketGalleryPage;
